import React from 'react/addons';
import shallowEqual from 'react/lib/shallowEqual';

function createStoreListener(getter, store, flux) {
  return function() {
    if (this.isMounted()) {
      const state = getter(store, this.props, flux);
      this.setState(state);
    }
  };
}

function getStore(flux, name) {
  const store = flux.store(name);
  if (typeof store === 'undefined') {
    throw new Error(
      `createContainer(): Store with name '${name}' does not exist.`
    );
  }

  return store;
}

/**
 * `createContainer()`
 *
 * Create a container React component that listens to changes in stores and
 * passes defined state as properties to a component.
 *
 * Example:
 *
 *   const HomeContainer = createContainer(Home, {
 *     pure: true, // if container can be a pure component
 *     stores: {
 *       // the key corresponds to the store's name
 *       'Homes': (store, props, flux) => {
 *         // return props to be passed to the component
 *         return {
 *           home: store.state.get(props.homeId)
 *         }
 *       }
 *     }
 *   });
 *
 *   // render
 *   <div>
 *      <HomeContainer homeId={homeId} />
 *   </div>
 *
 */
export function createContainer(Component, options) {
  if (typeof options === 'undefined') {
    options = {};
  }

  const Container = React.createClass({
    displayName: Component.displayName + "Container",
    contextTypes: {
      flux: React.PropTypes.object
    },

    childContextTypes: {
      flux: React.PropTypes.object
    },

    getChildContext() {
      return {
        flux: this.getFlux()
      };
    },

    _fluxStateGetters: [],
    _fluxListeners: {},

    getFlux() {
      return this.props.flux || (this.context && this.context.flux);
    },

    getInitialState() {
      const flux = this.getFlux();

      if (!flux) {
        throw new Error(
          `${this.constructor.displayName}: Could not find Flux instance. Ensure that your component has either \`this.context.flux\` or \`this.props.flux\`.`
        );
      }

      const passthrough = () => {};

      for (let storeName in options.stores) {
        const store = getStore(flux, storeName);
        let getter = options.stores[storeName];

        if (typeof getter !== 'function') {
          getter = passthrough; // return an empty object
        }

        this._fluxStateGetters.push({ store, getter });
        const listener = createStoreListener(getter, store, flux).bind(this);

        store.on('change', listener);
        this._fluxListeners[storeName] = listener;
      }

      const handlers = (typeof options.handlers !== 'undefined') ? options.handlers(flux.actions, flux) : {};

      return Object.assign({}, this.getStoreState(this.props), handlers);
    },

    getStoreState(props) {
      const flux = this.getFlux();

      return this._fluxStateGetters.reduce((result, stateGetter) => {
        const { getter, store } = stateGetter;
        const stateFromStores = getter(store, props, flux);
        return Object.assign(result, stateFromStores);
      }, {});
    },

    componentWillReceiveProps(nextProps) {
      this.setState(this.getStoreState(nextProps));
    },

    componentWillUnmount() {
      const flux = this.getFlux();

      for (let storeName in options.stores) {
        if (!this._fluxListeners.hasOwnProperty(storeName)) {
          continue;
        }

        const store = flux.store(storeName);
        if (typeof store === 'undefined') {
          continue;
        }

        const listener = this._fluxListeners[storeName];
        store.removeListener('change', listener);
      }
    },

    shouldComponentUpdate(nextProps, nextState) {
      if (options.pure === true) {
        return (
          !shallowEqual(this.props, nextProps) ||
          !shallowEqual(this.state, nextState)
        );
      }

      // otherwise always return true
      return true;
    },

    render() {
      return React.createElement(
        Component,
        Object.assign({ flux: this.getFlux() }, this.props, this.state, {ref: 'component'}),
      );
    }
  });

  return Container;
}



/** WEBPACK FOOTER **
 ** ./static/js/onefinestay/flux.js
 **/